'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';
import {
  detectRegion,
  getConsent,
  saveConsent,
  createConsentRecord,
  logConsentAction,
  type ConsentRegion,
} from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

export default function CookieBanner() {
  const [region, setRegion] = useState<ConsentRegion>('none');
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const detectedRegion = detectRegion();
    setRegion(detectedRegion);

    const existingConsent = getConsent();
    if (!existingConsent && detectedRegion !== 'none') {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const record = createConsentRecord(region, {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
    saveConsent(record);
    logConsentAction('accept_all', region);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const record = createConsentRecord(region, {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
    saveConsent(record);
    logConsentAction('reject_all', region);
    setShowBanner(false);
  };

  const handleCCPADismiss = () => {
    const record = createConsentRecord(region, {
      necessary: true,
      analytics: true,
      marketing: false,
      preferences: true,
    });
    saveConsent(record);
    logConsentAction('ccpa_dismiss', region);
    setShowBanner(false);
  };

  if (!showBanner || region === 'none') return null;

  return (
    <>
      {region === 'gdpr' && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-6">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">Cookie Consent</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    We use cookies to enhance your experience. By continuing to browse, you agree to our use of cookies.
                    Learn more in our{' '}
                    <Link href="/privacy" className="text-blue-600 hover:underline">
                      Privacy Policy
                    </Link>
                    {' '}and{' '}
                    <Link href="/cookies" className="text-blue-600 hover:underline">
                      Cookie Policy
                    </Link>
                    .
                  </p>
                </div>
                <button
                  onClick={() => setShowBanner(false)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  aria-label="Close banner"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={handleRejectAll}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Reject All
                </button>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {region === 'ccpa' && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <p className="text-sm text-gray-600">
                We respect your privacy. You have the right to opt out of the sale of your personal information.
                {' '}
                <Link href="/privacy#do-not-sell" className="text-blue-600 hover:underline">
                  Do Not Sell My Personal Information
                </Link>
              </p>
              <button
                onClick={handleCCPADismiss}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreferences && (
        <ConsentPreferenceCenter
          isOpen={showPreferences}
          onClose={() => setShowPreferences(false)}
          region={region}
        />
      )}
    </>
  );
}
